import { useEffect, useRef } from 'react';
import { imageUrl } from '../lib/images';

export default function Lightbox({
  isOpen,
  images = [],
  captions = [],
  currentIndex = 0,
  onClose,
  onPrev,
  onNext,
  onSelectIndex
}) {
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && images.length > 1) onPrev();
      if (e.key === 'ArrowRight' && images.length > 1) onNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, images.length, onClose, onPrev, onNext]);

  if (!isOpen || images.length === 0) return null;

  const currentImg = images[currentIndex];
  const currentCaption = captions[currentIndex] || '';

  const handleTouchStart = (e) => {
    touchStartX.current = e.changedTouches[0].screenX;
  };

  const handleTouchEnd = (e) => {
    touchEndX.current = e.changedTouches[0].screenX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0 && images.length > 1) {
        onNext();
      } else if (diff < 0 && images.length > 1) {
        onPrev();
      }
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Billedvisning"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/90 backdrop-blur-md p-4 md:p-8 animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Top bar */}
      <div className="absolute top-4 right-4 z-50 flex items-center gap-3">
        <span className="text-xs text-slate-300 font-mono bg-slate-900/80 px-3 py-1.5 rounded-full border border-slate-700/60">
          {currentIndex + 1} / {images.length}
        </span>
        <button
          type="button"
          onClick={onClose}
          className="w-10 h-10 rounded-full bg-slate-900/80 text-white hover:bg-slate-800 border border-slate-700/60 flex items-center justify-center transition cursor-pointer"
          aria-label="Luk billedvisning"
        >
          <i className="fa-solid fa-xmark text-lg" aria-hidden="true" />
        </button>
      </div>

      {/* Prev button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-slate-900/80 text-white hover:bg-slate-800 border border-slate-700/60 flex items-center justify-center transition shadow-xl cursor-pointer"
          aria-label="Forrige billede"
        >
          <i className="fa-solid fa-chevron-left text-lg" aria-hidden="true" />
        </button>
      )}

      {/* Next button */}
      {images.length > 1 && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-50 w-12 h-12 rounded-full bg-slate-900/80 text-white hover:bg-slate-800 border border-slate-700/60 flex items-center justify-center transition shadow-xl cursor-pointer"
          aria-label="Næste billede"
        >
          <i className="fa-solid fa-chevron-right text-lg" aria-hidden="true" />
        </button>
      )}

      {/* Main Image container */}
      <div
        className="relative max-w-5xl max-h-[85vh] flex flex-col items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <img
          src={imageUrl(currentImg, 'width=1600,quality=90,format=auto')}
          alt={currentCaption || 'Duus Rustfri projektbillede'}
          className="max-h-[75vh] w-auto max-w-full object-contain rounded-lg border border-slate-800 shadow-2xl select-none"
        />

        {currentCaption && (
          <p className="mt-3 text-center text-sm md:text-base text-slate-200 max-w-2xl px-4 py-1.5 rounded-lg bg-slate-900/80 border border-slate-800">
            {currentCaption}
          </p>
        )}

        {/* Thumbnails strip */}
        {images.length > 1 && (
          <div className="mt-4 flex items-center gap-2 overflow-x-auto max-w-full px-2 py-1 scrollbar-thin">
            {images.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onSelectIndex(idx)}
                className={`relative w-14 h-14 rounded-md overflow-hidden flex-shrink-0 border-2 transition cursor-pointer ${
                  idx === currentIndex
                    ? 'border-sky-400 scale-105 ring-2 ring-sky-400/40'
                    : 'border-slate-700 opacity-60 hover:opacity-100'
                }`}
                aria-label={`Vis billede ${idx + 1}`}
              >
                <img
                  src={imageUrl(img, 'width=120,quality=70,format=auto')}
                  alt=""
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
